/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { TranslatedCMLViewer, TranslatedPlainText } from '@coursera-web/ondemand-translation';
import moment from 'moment';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import _ from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { Theme } from '@coursera/cds-core';
import { Link, Typography, withTheme } from '@coursera/cds-core';

import type { CmlContent } from 'bundles/cml';
import SoftDelete from 'bundles/discussions/components/SoftDelete';
import type { ForumCloseState } from 'bundles/discussions/components/forumsV2-ForumThreads/__providers__/ForumPostDataProvider/__types__';
import type { AuthorProfile, Contributor } from 'bundles/discussions/lib/types';
import { TrackedReactLink } from 'bundles/page/components/TrackedLink2';

import { ForumThreadCard } from '../personalization-ForumThreadCard';

type FullProfile = {};

type ThreadEntry = {
  content: { question: string; details: CmlContent };
  isUpvoted: boolean;
  upvoteCount: number;
  createdAt: number;
  creator: AuthorProfile;
  replyCount: number;
  state: { closed: ForumCloseState };
};

type ThreadsListEntryProps = {
  threadUrl: string;
  profile: Contributor;
  timestamp: number;
  threadName: string;
  questionId: string;
  questionUserId: string;
  fullProfile: FullProfile;
  badge?: string;
  metadataLabel?: string;
  entry?: ThreadEntry;
  isPinned?: boolean;
  isDeleted?: boolean;
  isRead?: boolean;
  viewCount?: number;
  answerCount?: number;
  forumName?: string;
  enableTranslation?: boolean;
};

type WithTheme = {
  theme: Theme;
};

const styles = {
  listitem: css`
    padding: var(--cds-spacing-300) 0;
    position: relative;
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);

    &:last-child {
      border-bottom: none;
    }

    &.focused,
    &:hover,
    &.focused a {
      text-decoration: none;
    }

    .forReader {
      height: 0;
      width: 0;
    }
  `,
  translatedCmlViewer: css`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
  `,
};

class ThreadsListEntry extends React.Component<ThreadsListEntryProps & WithTheme> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof ThreadsListEntry.contextTypes>;

  static defaultProps = {
    isPinned: false,
    isDeleted: false,
    isRead: true,
  };

  state = {
    threadFocus: false,
  };

  handleThreadFocus = () => {
    this.setState({ threadFocus: true });
  };

  handleThreadBlur = () => {
    this.setState({ threadFocus: false });
  };

  render() {
    const {
      entry,
      isPinned,
      isDeleted,
      isRead,
      threadUrl,
      viewCount,
      answerCount,
      badge,
      timestamp,
      questionId,
      questionUserId,
      enableTranslation = true,
    } = this.props;
    const { threadFocus } = this.state;

    const threadName = entry?.content.question || '';

    const hasViewCount = typeof viewCount !== 'undefined' && !Number.isNaN(viewCount);
    const hasAnswerCount = typeof answerCount !== 'undefined' && !Number.isNaN(answerCount);
    const threadLabel = `${isPinned ? 'Pinned ' : ''}Thread: ${threadName}, 
                          ${moment(timestamp).fromNow()}
                          ${hasViewCount ? `, ${viewCount} views` : ''} 
                          ${hasAnswerCount ? `, ${answerCount} replies` : ''}`;

    return (
      <li
        css={styles.listitem}
        className={`rc-personalization_DiscussionsBody_ThreadsListEntry ${threadFocus ? 'focused' : 'blurred'}`}
      >
        {this.props.entry && (
          <ForumThreadCard
            title={this.props.entry.content.question}
            pinned={isPinned}
            titleLink={
              (threadUrl && (
                <Typography variant="h2semibold" className="rc-personalization_ForumThreadCard__title">
                  <Link
                    component={TrackedReactLink}
                    href={threadUrl}
                    trackingName="thread_list_entry"
                    aria-label={threadLabel}
                    data={{
                      question_id: questionId,
                      question_user_id: questionUserId,
                    }}
                  >
                    <TranslatedPlainText
                      enableTranslation={enableTranslation}
                      plainText={this.props.entry.content.question}
                    />
                  </Link>
                </Typography>
              )) ||
              undefined
            }
            threadUrl={threadUrl}
            description={
              <TranslatedCMLViewer
                css={styles.translatedCmlViewer}
                enableTranslation={enableTranslation}
                cml={this.props?.entry?.content?.details}
              />
            }
            isUpvoted={this.props.entry.isUpvoted}
            upvoteCount={this.props.entry.upvoteCount}
            forumQuestionId={this.props.questionId}
            creator={this.props.entry.creator}
            createdAt={this.props.entry.createdAt}
            replyCount={this.props.answerCount}
            lastContributor={this.props.profile}
            lastReplyDate={this.props.timestamp}
            answerCount={this.props.answerCount}
            badge={badge}
            isRead={isRead}
            isClosed={!!(this.props.entry && this.props.entry?.state?.closed)}
          />
        )}

        {isDeleted && <SoftDelete entry={entry} hideUndoDelete={true} />}
      </li>
    );
  }
}

type PropsFromStores = {
  fullProfile: unknown;
  courseId: string;
};

type PropsFromCaller = {
  profile: Contributor;
};

type PropsToComponent = PropsFromCaller & PropsFromStores;

export default _.compose(
  connectToStores<PropsToComponent, PropsFromCaller>(
    ['CourseStore', 'ClassmatesProfileStore'],
    ({ CourseStore, ClassmatesProfileStore: classmatesProfileStore }, { profile }) => {
      return {
        courseId: CourseStore.getCourseId(),
        fullProfile: _(classmatesProfileStore.profiles).findWhere({ externalId: profile?.externalUserId }),
      };
    }
  )
)(withTheme(ThreadsListEntry));
